import * as THREE from 'three';
import { fullGeometryOf } from './modeVisibility';

interface Entry {
  node: THREE.Object3D;
  meshes: THREE.Mesh[];
  bounds: THREE.Box3;
  local: THREE.Box3;
  scale: string;
}

/** What a ray can land on: walls, floors, items, openings and the walls' plinths and moldings. */
const PICKED = new Set(['wall', 'floor', 'item', 'opening', 'plinth', 'molding']);

export class SceneIndex {
  private entries = new Map<string, Entry>();
  version = 0;
  resourceVersion = 0;

  get(name: string): THREE.Object3D | undefined { return this.entries.get(name)?.node; }
  values(): IterableIterator<Entry> { return this.entries.values(); }
  clear(): void { this.entries.clear(); this.version++; this.resourceVersion++; }
  remove(name: string): void { if (this.entries.delete(name)) { this.version++; this.resourceVersion++; } }

  upsert(node: THREE.Object3D): void {
    if (this.entries.get(node.name)?.node === node) { this.update(node.name); return; }
    const meshes: THREE.Mesh[] = [];
    node.traverse(object => { if ((object as THREE.Mesh).isMesh) meshes.push(object as THREE.Mesh); });
    this.entries.set(node.name, { node, meshes, bounds: new THREE.Box3(), local: new THREE.Box3(), scale: '' });
    this.update(node.name); this.version++; this.resourceVersion++;
  }

  update(name: string): void {
    const entry = this.entries.get(name); if (!entry) return;
    entry.node.updateWorldMatrix(true, true);
    const scale = entry.node.scale.toArray().join(','), rebuildLocal = entry.scale !== scale;
    const inverse = rebuildLocal ? entry.node.matrixWorld.clone().invert() : null;
    const rootScale = rebuildLocal ? new THREE.Matrix4().makeScale(...entry.node.scale.toArray() as [number, number, number]) : null;
    entry.bounds.makeEmpty(); if (rebuildLocal) entry.local.makeEmpty();
    for (const mesh of entry.meshes) {
      if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox();
      if (mesh.geometry.boundingBox) entry.bounds.union(mesh.geometry.boundingBox.clone().applyMatrix4(mesh.matrixWorld));
      if (inverse && rootScale) {
        const full = fullGeometryOf(mesh); if (!full.boundingBox) full.computeBoundingBox();
        if (full.boundingBox) entry.local.union(full.boundingBox.clone().applyMatrix4(rootScale.clone().multiply(inverse).multiply(mesh.matrixWorld)));
      }
    }
    entry.scale = scale; this.version++;
  }

  bounds(name: string): THREE.Box3 | undefined { return this.entries.get(name)?.bounds; }
  localBounds(name: string): THREE.Box3 | undefined { return this.entries.get(name)?.local; }
  meshes(name: string): readonly THREE.Mesh[] { return this.entries.get(name)?.meshes ?? []; }

  candidates(ray: THREE.Ray): THREE.Mesh[] {
    const result: THREE.Mesh[] = [];
    for (const entry of this.entries.values()) {
      if (!ray.intersectsBox(entry.bounds)) continue;
      for (const mesh of entry.meshes) {
        let visible = true;
        for (let node: THREE.Object3D | null = mesh; node; node = node.parent) if (!node.visible) { visible = false; break; }
        if (visible && PICKED.has(mesh.userData.kind)) result.push(mesh);
      }
    }
    return result;
  }
}
